import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { payeesApi, type PaginatedPayeesResponse } from "@/lib/api";
import type { Payee } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";

interface UsePayeesOptions {
  enabled?: boolean;
  name?: string;
  isActive?: boolean;
  country?: string;
  city?: string;
  partyId?: string;
  pageSize?: number;
  page?: number;
  search?: string;
  ordering?: string;
}

interface UsePayeesReturn {
  payees: Payee[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

const createPayeesQueryKey = (
  labelId: string | undefined,
  name: string | undefined,
  isActive: boolean | undefined,
  country: string | undefined,
  city: string | undefined,
  partyId: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  search: string | undefined,
  ordering: string | undefined,
) => {
  const key: (string | number | boolean)[] = ["payees"];

  if (labelId) key.push("label", labelId);

  const normalizedName = name?.trim() || null;
  if (normalizedName) key.push("name", normalizedName);

  if (isActive !== undefined) key.push("isActive", isActive);
  if (country) key.push("country", country);
  if (city) key.push("city", city);
  if (partyId) key.push("partyId", partyId);
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);
  if (page && page > 1) key.push("page", page);
  if (search) key.push("search", search);
  if (ordering) key.push("ordering", ordering);

  return key;
};

export const usePayees = ({
  enabled = true,
  name,
  isActive,
  country,
  city,
  partyId,
  pageSize,
  page,
  search,
  ordering,
}: UsePayeesOptions = {}): UsePayeesReturn => {
  const { currentLabel } = useOrganizationStore();

  const filters = {
    label_id: currentLabel?.id,
    ...(name && { name }),
    ...(isActive !== undefined && { is_active: isActive }),
    ...(country && { country }),
    ...(city && { city }),
    ...(partyId && { party_id: partyId }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(search && { q: search }),
    ...(ordering && { ordering }),
  };

  const queryKey = createPayeesQueryKey(
    currentLabel?.id,
    name,
    isActive,
    country,
    city,
    partyId,
    pageSize,
    page,
    search,
    ordering,
  );

  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedPayeesResponse> => {
      return await payeesApi.getPayees(filters);
    },
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    payees: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
